import { baseURL } from '../../../api/baseUrl';
import {
  CMSApiResponse,
  CMSSearchResponse,
  CMSViewMode
} from '../types/CMSTypes';

// ===== CORE CMS API FUNCTIONS =====

// Search for categories by keyword
export const searchCategoryNames = async (keyword: string): Promise<CMSSearchResponse> => {
  try {
    const response = await baseURL.get(`/cms-category/search-names/${encodeURIComponent(keyword)}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error searching category names:', error);
    throw error;
  }
};

// Get CMS page data for a specific category
export const getCMSPage = async (
  categoryId: string,
  onlyPublished: boolean = true
): Promise<CMSApiResponse> => {
  try {
    const response = await baseURL.get(
      `/cms-category/${categoryId}/cms-page/${onlyPublished}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching CMS page:', error);
    throw error;
  }
};

// ===== HOME PAGE SPECIFIC API =====

// Get Home page data
export const getHomePageData = async (viewMode: CMSViewMode = 'live'): Promise<CMSApiResponse> => {
  try {
    // First, search for the home category
    const searchResponse = await searchCategoryNames('home');

    if (!searchResponse.success || !searchResponse.non_collection_results) {
      throw new Error('Home category not found');
    }

    const homeCategory = searchResponse.non_collection_results.find(
      category => category.category_name.toLowerCase() === 'home' &&
        category.category_type === 'home'
    );

    if (!homeCategory) {
      throw new Error('Home category not found');
    }

    // For consumer site, always use published content only (no draft mode for public)
    const onlyPublished = true;

    // Get the CMS page data
    const cmsResponse = await getCMSPage(homeCategory.id, onlyPublished);

    if (!cmsResponse.success) {
      throw new Error('Failed to load home page CMS data');
    }

    return cmsResponse;

  } catch (error) {
    console.error('Error fetching home page data:', error);
    throw error;
  }
};

// ===== ITEM GROUP PAGE SPECIFIC API =====

// Get CMS data for categories by category name with preferred category type
export const getCategoryPageData = async (
  categoryName: string,
  viewMode: CMSViewMode = 'live',
  preferredCategoryType?: string
): Promise<CMSApiResponse> => {
  try {
    // First, search for categories by category name
    const searchResponse = await searchCategoryNames(categoryName);

    if (!searchResponse.success) {
      throw new Error(`Category '${categoryName}' not found`);
    }

    // Collect all potential matches from all result arrays
    const allMatches = [
      ...(searchResponse.non_collection_results || []),
      ...(searchResponse.collection_results || []),
      ...(searchResponse.application_results || []),
      ...(searchResponse.item_group_results || []),
      ...(searchResponse.brand_results || []),
      ...(searchResponse.group_results || [])
    ].filter(category =>
      category.category_name.toLowerCase() === categoryName.toLowerCase()
    );

    if (allMatches.length === 0) {
      throw new Error(`Category '${categoryName}' not found`);
    }

    let categoryMatch = null;

    // If a preferred category type is specified, look for it first
    if (preferredCategoryType) {
      categoryMatch = allMatches.find(category =>
        category.category_type === preferredCategoryType
      );

      if (categoryMatch) {
        console.log(`Selected preferred category type: ${preferredCategoryType} for ${categoryName}`);
      }
    }

    // If no preferred type match found, use default priority order
    if (!categoryMatch) {
      const categoryTypePriority = ['item_group', 'group', 'collection', 'application', 'brand', 'home'];

      for (const priorityType of categoryTypePriority) {
        categoryMatch = allMatches.find(category => category.category_type === priorityType);
        if (categoryMatch) {
          console.log(`Selected category type: ${priorityType} for ${categoryName}`);
          break;
        }
      }
    }

    // Fallback to first match if no priority match found
    if (!categoryMatch) {
      categoryMatch = allMatches[0];
      console.warn(`No priority match found, using first match: ${categoryMatch.category_type}`);
    }

    // For consumer site, always use published content only
    const onlyPublished = true;

    const cmsResponse = await getCMSPage(categoryMatch.id, onlyPublished);

    if (!cmsResponse.success) {
      throw new Error(`Failed to load CMS data for category '${categoryName}'`);
    }

    return cmsResponse;

  } catch (error) {
    console.error(`Error fetching category '${categoryName}' page data:`, error);
    throw error;
  }
};

// Backward compatibility - map item_group codes to category names
const ITEM_GROUP_TO_CATEGORY_MAP: Record<string, string> = {
  '000': 'homeware',
  '001': 'wallpaper',
  '004': 'rugs',
  '005': 'fabric',
  '006': 'bedroom',
  '007': 'illumination',
  '008': 'tableware',
  '009': 'furniture',
  '010': 'decorative',
  '013': 'Throws, Quilts and Furs',
  '014': 'wall decor',
  '015': 'curtains',
  '016': 'bathroom',
  '018': 'scatters'
};

// Get CMS data for item group categories (backward compatibility)
export const getItemGroupPageData = async (itemGroup: string, viewMode: CMSViewMode = 'live'): Promise<CMSApiResponse> => {
  const categoryName = ITEM_GROUP_TO_CATEGORY_MAP[itemGroup];

  if (!categoryName) {
    throw new Error(`Item group '${itemGroup}' not mapped to category name`);
  }

  return getCategoryPageData(categoryName, viewMode);
};

// ===== CATEGORY LINK BUILDING =====

// Get category details for link building
export const getCategoryDetails = async (categoryId: string, viewMode: CMSViewMode = 'live'): Promise<CMSApiResponse> => {
  try {
    const onlyPublished = true; // Consumer always uses published content

    const response = await baseURL.get(
      `/cms-category/${categoryId}/cms-page/${onlyPublished}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching category details:', error);
    throw error;
  }
};

// Build the correct URL for a category based on its type and data
export const buildCategoryUrl = (categoryData: any): string => {
  const { category_type, item_group, application, collection, brand, category_name } = categoryData;

  switch (category_type) {
    case 'collection':
      if (item_group && collection) {
        return `/item_group/${item_group}/collection/${encodeURIComponent(collection)}`;
      }
      break;

    case 'application':
      if (item_group && application) {
        return `/item_group/${item_group}/application/${encodeURIComponent(application)}`;
      }
      break;

    case 'item_group':
      if (item_group) {
        return `/item_group/${item_group}`;
      }
      break;

    case 'brand':
      if (brand) {
        return `/brand/${encodeURIComponent(brand)}`;
      }
      break;

    default:
      // Fallback to category name based URL
      return `/category/${encodeURIComponent(category_name)}`;
  }

  // Ultimate fallback
  return `/category/${categoryData.id}`;
};